import React, { useState, useMemo, useEffect } from 'react';
import { useAuth, useFirebase } from '../context/AuthContext';
import { collection, query, onSnapshot, orderBy, doc } from 'firebase/firestore';
import { NavLink } from 'react-router-dom';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { FileText, Edit, PlusSquare, Loader, ChevronLeft, ChevronRight, Target } from 'lucide-react';
import CpdProgressBar from '../components/CpdProgressBar';
import CpdTimelineChart from '../components/CpdTimelineChart';
import CpdProjection from '../components/CpdProjection';
import SuggestedActivities from '../components/SuggestedActivities';
import logo from '../assets/logo.png';
import { defaultCpdRequirement } from '../data/cpdTemplates';

// New component to display progress for grouped categories
const CpdCategoryGroupProgress = ({ categoryGroups, categoryHours }) => {
    if (!categoryGroups || categoryGroups.length === 0) {
        return null;
    }

    const groupProgress = categoryGroups.map(group => {
        const completedHours = group.mapsFrom.reduce((acc, categoryName) => {
            return acc + (categoryHours[categoryName] || 0);
        }, 0);
        return {
            ...group,
            completedHours,
            percentage: group.requiredHours > 0 ? (completedHours / group.requiredHours) * 100 : 0,
        };
    });

    return (
        <div className="bg-gray-50 dark:bg-gray-800 p-6 rounded-lg shadow-md border border-gray-200 dark:border-gray-700">
            <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-4 flex items-center">
                <Target size={20} className="mr-2 text-blue-500" />
                Category Group Requirements
            </h3>
            <div className="space-y-4">
                {groupProgress.map(group => (
                    <div key={group.groupName}>
                        <div className="flex justify-between items-baseline mb-1">
                            <span className="text-base font-medium text-gray-700 dark:text-gray-300">{group.groupName}</span>
                            <span className="text-sm font-semibold text-gray-600 dark:text-gray-400">
                                {group.completedHours.toFixed(1)} / {group.requiredHours} hrs
                            </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2.5 dark:bg-gray-700">
                            <div 
                                className="bg-blue-600 h-2.5 rounded-full" 
                                style={{ width: `${Math.min(group.percentage, 100)}%` }}
                            ></div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};


const CpdLogPage = ({ onEditLog }) => {
    const { user } = useAuth();
    const { db } = useFirebase();
    const [logs, setLogs] = useState([]);
    const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
    const [cpdRequirements, setCpdRequirements] = useState(defaultCpdRequirement);

    useEffect(() => {
        if (!user || !db || user.isAnonymous) return;
        const settingsRef = doc(db, 'users', user.uid, 'settings', 'cpdRequirements');
        const unsubscribe = onSnapshot(settingsRef, (docSnap) => {
            if (docSnap.exists() && docSnap.data().name) {
                setCpdRequirements(docSnap.data());
            } else {
                setCpdRequirements(defaultCpdRequirement);
            }
        });
        return () => unsubscribe();
    }, [user, db]);

    const [searchFilter, setSearchFilter] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('All');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    useEffect(() => {
        if (!db || !user || user.isAnonymous) {
            setLogs([]);
            return;
        }
        const logsRef = collection(db, 'users', user.uid, 'cpdLogs');
        const q = query(logsRef, orderBy('completedAt', 'desc'));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const fetchedLogs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setLogs(fetchedLogs);

            if (fetchedLogs.length > 0) {
                const dates = fetchedLogs
                    .map(log => log.activityStartDate && new Date(log.activityStartDate))
                    .filter(date => date && !isNaN(date));
                
                if (dates.length > 0) {
                    const earliestDate = new Date(Math.min(...dates));
                    setStartDate(earliestDate.toISOString().split('T')[0]);
                    setEndDate(new Date().toISOString().split('T')[0]);
                }
            }
        });
        return () => unsubscribe();
    }, [db, user]);

    const { 
        totalHours, 
        categoryHours, 
        timelineData,
        filteredLogs,
        pageCount,
        paginatedLogs,
        uniqueCategories,
    } = useMemo(() => {
        const cycleYears = cpdRequirements.cycleYears || 1;
        const currentYear = new Date().getFullYear();
        const cycleStartDate = new Date(currentYear - cycleYears + 1, 0, 1);

        const completed = logs.filter(log => {
            const logDate = log.activityStartDate ? new Date(log.activityStartDate) : null;
            return log.status === 'Completed' && logDate && logDate >= cycleStartDate;
        });

        const finalFilteredLogs = completed.filter(log => {
            const logDate = log.activityStartDate ? new Date(log.activityStartDate) : null;
            const start = startDate ? new Date(startDate) : null;
            const end = endDate ? new Date(endDate) : null;
            if (start) start.setHours(0, 0, 0, 0);
            if (end) end.setHours(23, 59, 59, 999);

            const dateMatch = !start || !end || !logDate ? true : logDate >= start && logDate <= end;
            const categoryMatch = categoryFilter === 'All' || log.activityType === categoryFilter;
            const searchMatch = searchFilter === '' || (log.activityCompleted || log.articleTitle || '').toLowerCase().includes(searchFilter.toLowerCase());

            return dateMatch && categoryMatch && searchMatch;
        });

        const newPageCount = Math.ceil(finalFilteredLogs.length / itemsPerPage);
        if (currentPage > newPageCount && newPageCount > 0) {
            setCurrentPage(newPageCount);
        }
        const newPaginatedLogs = finalFilteredLogs.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
        
        const total = completed.reduce((acc, log) => acc + (Number(log.hours) || 0), 0);
        
        const individualCategoryHours = completed.reduce((acc, log) => {
            const type = log.activityType || 'Other';
            acc[type] = (acc[type] || 0) + (Number(log.hours) || 0);
            return acc;
        }, {});
        
        const yearsInCycle = Array.from({ length: cycleYears }, (_, i) => currentYear - i).reverse();
        const yearlyHours = yearsInCycle.map(year => {
            const totalHoursForYear = completed
                .filter(log => new Date(log.activityStartDate).getFullYear() === year)
                .reduce((acc, log) => acc + (Number(log.hours) || 0), 0);
            return { month: year.toString(), hours: totalHoursForYear };
        });

        return {
            totalHours: total,
            categoryHours: individualCategoryHours,
            timelineData: yearlyHours,
            filteredLogs: finalFilteredLogs,
            pageCount: newPageCount || 1,
            paginatedLogs: newPaginatedLogs,
            uniqueCategories: ['All', ...new Set(logs.map(log => log.activityType).filter(Boolean))]
        };
    }, [logs, currentPage, startDate, endDate, categoryFilter, searchFilter, cpdRequirements]);

    const fileToDataUrl = (file) => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
    });

    const urlToBase64 = async (url) => {
        const response = await fetch(url);
        const blob = await response.blob();
        return fileToDataUrl(blob);
    };
    
    const generatePdf = async () => {
        if (!user) return;
        setIsGeneratingPdf(true);

        if (filteredLogs.length === 0) {
            alert("No completed CPD activities found in the selected filters to export.");
            setIsGeneratingPdf(false);
            return;
        }
        
        const doc = new jsPDF({ orientation: 'landscape' });
        let lastY = 14;

        try {
            const logoBase64 = await urlToBase64(logo);
            const imgProps = doc.getImageProperties(logoBase64);
            const logoAspectRatio = imgProps.width / imgProps.height;
            const logoWidth = 45;
            const logoHeight = logoWidth / logoAspectRatio; 
            doc.addImage(logoBase64, 'PNG', 240, 8, logoWidth, logoHeight);
            lastY = Math.max(lastY, 8 + logoHeight);
        } catch (error) {
            console.error("Error adding logo to PDF:", error);
        }

        doc.setFontSize(18);
        doc.setTextColor(40);
        doc.text("Continuing Professional Development (CPD) Log", 14, 20);
        doc.setFontSize(12);
        doc.text(`User: ${user?.displayName || user?.email}`, 14, 28);
        lastY = Math.max(lastY, 28);

        const tableColumn = ["Learning goals", "Completed CPD activities", "Date", "Number and category of hours", "Reflection on learning and impact on practice"];
        const tableRows = [];

        filteredLogs.forEach(log => {
            let completedActivityText = `${log.activityCompleted || ''}\nProvider/Source: ${log.providerSource || 'N/A'}`;
            if (log.evidenceUrl && (log.evidenceUrl.toLowerCase().includes('.png') || log.evidenceUrl.toLowerCase().includes('.jpg') || log.evidenceUrl.toLowerCase().includes('.jpeg'))) {
                completedActivityText += `\nEvidence: See attached image.`;
            } else if (log.evidenceUrl) {
                completedActivityText += `\nEvidence Link: ${log.evidenceUrl}`;
            }

            const logData = [
                log.learningGoal || '',
                completedActivityText,
                log.activityStartDate ? new Date(log.activityStartDate).toLocaleDateString() : 'N/A',
                `${log.hours || 0} hrs\nCategory: ${log.activityType || 'N/A'}`,
                `Reflection:\n${log.reflection || ''}\n\nImpact on Practice:\n${log.impactOnPractice || ''}`
            ];
            tableRows.push(logData);
        });

        autoTable(doc, {
            head: [tableColumn],
            body: tableRows,
            startY: lastY + 5,
            theme: 'grid',
            headStyles: { fillColor: [34, 197, 94], textColor: 255 },
            styles: { fontSize: 8, cellPadding: 2, overflow: 'linebreak' },
            columnStyles: { 4: { cellWidth: 80 } }
        });

        for (const log of filteredLogs) {
            const url = log.evidenceUrl;
            if (url && (url.toLowerCase().includes('.png') || url.toLowerCase().includes('.jpg') || url.toLowerCase().includes('.jpeg'))) {
                try {
                    doc.addPage();
                    doc.setFontSize(14);
                    doc.text(`Evidence for: ${log.activityCompleted || log.articleTitle}`, 14, 20);
                    const evidenceBase64 = await urlToBase64(url);
                    const imgProps = doc.getImageProperties(evidenceBase64);
                    const pdfWidth = doc.internal.pageSize.getWidth();
                    const pdfHeight = doc.internal.pageSize.getHeight();
                    const imgAspectRatio = imgProps.width / imgProps.height;
                    let imgWidth = pdfWidth - 30;
                    let imgHeight = imgWidth / imgAspectRatio;
                    if (imgHeight > pdfHeight - 40) {
                        imgHeight = pdfHeight - 40;
                        imgWidth = imgHeight * imgAspectRatio;
                    }
                    doc.addImage(evidenceBase64, imgProps.fileType, 15, 30, imgWidth, imgHeight);
                } catch (error) {
                    console.error("Could not embed image:", error);
                    doc.text("Error: Could not embed attached image from URL.", 15, 30);
                }
            }
        }
        
        doc.save(`CPD_Log_${user.uid.slice(0, 8)}.pdf`);
        setIsGeneratingPdf(false);
    };

    const LogItem = ({ log, onEdit }) => (
        <div className="bg-gray-100 dark:bg-gray-800 p-4 rounded-lg shadow-md border border-gray-200 dark:border-gray-700">
            <div className="flex justify-between items-start">
                <div>
                    <h3 className="font-bold text-lg text-gray-800 dark:text-gray-200">{log.activityCompleted || log.articleTitle}</h3>
                    <p className="text-sm font-semibold text-blue-600 dark:text-blue-400">Goal: {log.learningGoal}</p>
                </div>
                <button onClick={() => onEdit(log)} className="flex items-center text-sm text-gray-500 hover:text-blue-500 p-1 -mt-1 -mr-1">
                    <Edit size={16} className="mr-1"/> Edit
                </button>
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-500 mt-2 pt-2 border-t border-gray-300 dark:border-gray-600 flex justify-between items-center">
                <div>
                    <span>Date: {log.activityStartDate ? new Date(log.activityStartDate).toLocaleDateString() : 'N/A'}</span>
                    <span className="ml-4">Category: {log.activityType}</span>
                </div>
                <span>Hours: <strong>{log.hours}</strong></span>
            </div>
        </div>
    );

    return (
        <div>
            <div className="text-center mb-8">
                <h1 className="text-2xl md:text-4xl font-bold text-gray-900 dark:text-cyan-400 mb-2">My CPD Dashboard</h1>
                <p className="text-gray-600 dark:text-gray-400 text-base md:text-lg">
                    Tracking your progress for the current {cpdRequirements.cycleYears || 1}-year cycle.
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <CpdProgressBar currentHours={totalHours} requiredHours={cpdRequirements.totalHours} title={`CPD Progress (${cpdRequirements.cycleYears || 1}-Year Cycle)`} />
                <CpdProjection currentHours={totalHours} requiredHours={cpdRequirements.totalHours} cycleYears={cpdRequirements.cycleYears || 1} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
                <CpdTimelineChart data={timelineData} label="CPD Hours per Year" />
                <CpdCategoryGroupProgress categoryGroups={cpdRequirements.categoryGroups} categoryHours={categoryHours} />
            </div>
            
            <div className="mt-8">
                <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-4">Completed Activities ({filteredLogs.length})</h2>
                
                <div className="p-4 bg-gray-100 dark:bg-gray-800 rounded-lg mb-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 items-end">
                        <div>
                            <label htmlFor="search-filter" className="text-sm font-medium">Search by Name</label>
                            <input id="search-filter" type="text" placeholder="e.g., SABR meeting..." value={searchFilter} onChange={e => setSearchFilter(e.target.value)} className="w-full p-2 mt-1 border rounded-md bg-white dark:bg-gray-900"/>
                        </div>
                        <div>
                            <label htmlFor="category-filter" className="text-sm font-medium">Filter by Category</label>
                            <select id="category-filter" value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)} className="w-full p-2 mt-1 border rounded-md bg-white dark:bg-gray-900">
                                {uniqueCategories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                            </select>
                        </div>
                        <div className="flex items-end gap-2">
                            <div className="flex-grow">
                                <label htmlFor="startDate" className="text-sm font-medium">From:</label>
                                <input id="startDate" type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="w-full p-2 mt-1 border rounded-md bg-white dark:bg-gray-900"/>
                            </div>
                            <div className="flex-grow">
                                <label htmlFor="endDate" className="text-sm font-medium">To:</label>
                                <input id="endDate" type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="w-full p-2 mt-1 border rounded-md bg-white dark:bg-gray-900"/>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="flex justify-end space-x-2">
                    <NavLink to="/cpd-assistant" className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold shadow-md hover:bg-blue-700">
                        <PlusSquare size={18} className="mr-2"/> Log New Activity
                    </NavLink>
                    <button onClick={generatePdf} disabled={isGeneratingPdf} className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg font-semibold shadow-md hover:bg-green-700 disabled:bg-gray-400">
                       {isGeneratingPdf ? <Loader size={18} className="animate-spin mr-2"/> : <FileText size={18} className="mr-2"/>}
                       {isGeneratingPdf ? 'Generating...' : 'Export PDF'}
                    </button>
                </div>
            </div>

            <div className="space-y-4 mt-6">
                {paginatedLogs.length > 0 ? (
                    paginatedLogs.map(log => <LogItem key={log.id || log.articleId} log={log} onEdit={onEditLog} />)
                ) : (
                    <div className="text-center text-gray-500 py-12">
                        <h3 className="text-lg font-semibold">No activities found for the selected filters.</h3>
                        <p>Try adjusting your search or date range.</p>
                    </div>
                )}
            </div>

             <div className="mt-6 flex justify-center items-center space-x-4">
                <button onClick={() => setCurrentPage(p => Math.max(p - 1, 1))} disabled={currentPage === 1} className="p-2 disabled:opacity-50"><ChevronLeft/></button>
                <span className="font-semibold">Page {currentPage} of {pageCount}</span>
                <button onClick={() => setCurrentPage(p => Math.min(p + 1, pageCount))} disabled={currentPage === pageCount} className="p-2 disabled:opacity-50"><ChevronRight/></button>
            </div>
            
            <SuggestedActivities categoryHours={categoryHours} />
        </div>
    );
};

export default CpdLogPage;